import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const guideLinks = [
  { label: "Poradnik Bezpieczeństwa", slug: "poradnik-bezpieczenstwa-kakobuy" },
  { label: "Przewodnik Wysyłki", slug: "przewodnik-wysylki-kakobuy-polska" },
  { label: "Dla Początkujących", slug: "przewodnik-poczatkujacy-kakobuy" },
  { label: "Jak Korzystać", slug: "jak-korzystac-kakobuy-spreadsheet" },
  { label: "Wszystkie Artykuły", slug: "kompletny-przewodnik-kakobuy-spreadsheet" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCategorySelect = (url: string) => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
    window.open(url, "_blank", "noopener,nofollow");
  };

  const handleGuideClick = (slug: string) => {
    setGuideDropdownOpen(false);
    setMobileMenuOpen(false);
    setMobileGuideOpen(false);
    navigate(`/artykul/${slug}`);
  };

  const handleGoHome = () => {
    setMobileMenuOpen(false);
    navigate("/");
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70" : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setMobileMenuOpen(false)}
          >
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-950 whitespace-nowrap">
              Kakobuy<span className="text-primary-500">Sheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-home-4-line mr-1"></i>
              {t("nav.home")}
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => {
                  setDropdownOpen(!dropdownOpen);
                  setGuideDropdownOpen(false);
                }}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-grid-line mr-1"></i>
                {t("nav.categories")}
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-xs ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden z-50 max-h-80 overflow-y-auto shadow-lg">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategorySelect(cat.url)}
                      className="w-full text-left px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors duration-150 cursor-pointer whitespace-nowrap border-b border-background-100 last:border-b-0"
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" ref={guideRef}>
              <button
                onClick={() => {
                  setGuideDropdownOpen(!guideDropdownOpen);
                  setDropdownOpen(false);
                }}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line mr-1"></i>
                {t("nav.guide")}
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-xs ${guideDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>
              {guideDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden z-50 shadow-lg">
                  {guideLinks.map((link) => (
                    <button
                      key={link.slug}
                      onClick={() => handleGuideClick(link.slug)}
                      className="w-full text-left px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors duration-150 cursor-pointer border-b border-background-100 last:border-b-0"
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => navigate("/artykul/kompletny-przewodnik-kakobuy-spreadsheet")}
              className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-article-line mr-1"></i>
              {t("nav.article")}
            </button>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer hover:scale-105"
            >
              {t("cta_bar.button2")}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 text-sm font-semibold text-primary-500 border-2 border-primary-500 hover:bg-primary-50 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Kakobuy Spreadsheet
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            <Link
              to="/"
              onClick={handleGoHome}
              className="flex items-center gap-2 text-sm font-medium text-foreground-700 py-3 px-2 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line text-primary-500"></i>
              {t("nav.home")}
            </Link>

            <div>
              <button
                onClick={() => {
                  setMobileCatOpen(!mobileCatOpen);
                  setMobileGuideOpen(false);
                }}
                className="flex items-center justify-between w-full text-sm font-medium text-foreground-700 py-3 px-2 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-grid-line text-primary-500"></i>
                  {t("nav.categories")}
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
              </button>
              {mobileCatOpen && (
                <div className="ml-4 pl-3 border-l-2 border-background-200 flex flex-col">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategorySelect(cat.url)}
                      className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 py-2.5 px-2 text-left cursor-pointer hover:bg-background-100 rounded-md transition-colors"
                    >
                      <span className="w-6 h-6 rounded-md overflow-hidden flex-shrink-0 bg-background-200">
                        <img src={cat.imageUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </span>
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <button
                onClick={() => {
                  setMobileGuideOpen(!mobileGuideOpen);
                  setMobileCatOpen(false);
                }}
                className="flex items-center justify-between w-full text-sm font-medium text-foreground-700 py-3 px-2 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-book-open-line text-primary-500"></i>
                  {t("nav.guide")}
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileGuideOpen ? "rotate-180" : ""}`}></i>
              </button>
              {mobileGuideOpen && (
                <div className="ml-4 pl-3 border-l-2 border-background-200 flex flex-col">
                  {guideLinks.map((link) => (
                    <button
                      key={link.slug}
                      onClick={() => handleGuideClick(link.slug)}
                      className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 py-2.5 px-2 text-left cursor-pointer hover:bg-background-100 rounded-md transition-colors"
                    >
                      <i className="ri-arrow-right-s-line text-xs"></i>
                      {link.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => {
                setMobileMenuOpen(false);
                navigate("/artykul/kompletny-przewodnik-kakobuy-spreadsheet");
              }}
              className="flex items-center gap-2 text-sm font-medium text-foreground-700 py-3 px-2 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-article-line text-primary-500"></i>
              {t("nav.article")}
            </button>

            <div className="flex flex-col gap-2 pt-4 mt-2 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full text-center transition-all duration-200 cursor-pointer"
              >
                {t("cta_bar.button2")}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-semibold text-primary-500 border-2 border-primary-500 hover:bg-primary-50 rounded-full text-center transition-all duration-200 cursor-pointer"
              >
                Kakobuy Spreadsheet
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}